import { getAppRoleLabel } from '../Utils/appRoleLabels';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import AddManagedUserDialog from '../Components/Shared/AddManagedUserDialog';
import ManagedUserDrawer from '../Components/Shared/ManagedUserDrawer';
import LoadingSpinner from '../Components/Elements/LoadingSpinner';
import { appColors } from '../Config/Theme';
import { useSecurity } from '../Contexts/SecurityProvider';
import { getLocations } from '../Services/LocationService';
import {
  getLatestEmployeeReconciliation,
  getManagedUser,
  getManagedUsers,
  getUserManagementErrorMessage,
  getUserManagementMetadata,
  getUserManagementRoleMatrix,
  searchEmployeeDirectory,
} from '../Services/UserManagementService';
import {
  EmployeeDirectoryEmployee,
  EmployeeReconciliationRun,
  ManagedUser,
  ManagedUsersPage,
  UserManagementMetadata,
  UserManagementRoleMatrix,
} from '../Types/UserManagement';
import { managedUserDisplayName } from '../Utils/managedUserDisplayName';
import AccessDeniedPage from './AccessDeniedPage';

const ROLE_COLORS: Record<string, { color: string; background: string }> = {
  SystemAdmin: { color: '#5B21B6', background: '#EDE9FE' },
  Admin: { color: '#5B21B6', background: '#F3E8FF' },
  IncidentCommander: { color: '#1D4ED8', background: '#DBEAFE' },
  OfficeDirector: { color: '#1D4ED8', background: '#E0F2FE' },
  Headcounter: { color: '#0F766E', background: '#CCFBF1' },
  BackupHeadcounter: { color: '#0369A1', background: '#E0F2FE' },
  RecallTeam: { color: '#0369A1', background: '#E0F2FE' },
  ReadOnlyViewer: { color: '#4B5563', background: '#F3F4F6' },
};

const initialsFor = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'U';

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const formatDateOnly = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));

const roleLabel = getAppRoleLabel;

const RoleChip: React.FC<{ roleName: string }> = ({ roleName }) => {
  const colors = ROLE_COLORS[roleName] ?? {
    color: appColors.text.body,
    background: appColors.surface.inactive,
  };
  return (
    <Chip
      label={roleLabel(roleName)}
      size="small"
      sx={{
        height: 24,
        bgcolor: colors.background,
        color: colors.color,
        borderRadius: '5px',
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
};

const SettingsUsersPage: React.FC = () => {
  const { security, isSecurityLoading, securityError, hasPermission } = useSecurity();
  const canView = hasPermission('UserManagement.View');
  const [metadata, setMetadata] = useState<UserManagementMetadata | null>(null);
  const [usersPage, setUsersPage] = useState<ManagedUsersPage | null>(null);
  const [roleMatrix, setRoleMatrix] = useState<UserManagementRoleMatrix[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleId, setRoleId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoleMatrix, setLoadingRoleMatrix] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [employeeToAdd, setEmployeeToAdd] = useState<EmployeeDirectoryEmployee | null>(null);
  const [directoryEmployees, setDirectoryEmployees] = useState<EmployeeDirectoryEmployee[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [latestReconciliation, setLatestReconciliation] = useState<EmployeeReconciliationRun | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchText);
      setPageNumber(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoadingMetadata(true);
    Promise.all([getUserManagementMetadata(), getLocations()])
      .then(([result, locationResult]) => {
        if (cancelled) return;

        // Use the same active U.S. location directory as /incidents/new. For
        // location-scoped administrators, keep only IDs authorized by the
        // user-management metadata endpoint.
        const authorizedLocationIds = new Set(
          result.locations.map((location) => location.locationId),
        );
        const locations = locationResult.isFallback
          ? result.locations
          : locationResult.locations
              .filter((location) =>
                location.isActive &&
                location.country === 'United States' &&
                (
                  result.capabilities.canViewGlobal ||
                  authorizedLocationIds.has(location.locationId)
                ),
              )
              .sort((a, b) =>
                a.locationName.localeCompare(b.locationName) ||
                (a.stateProvince ?? '').localeCompare(b.stateProvince ?? ''),
              )
              .map((location) => ({
                locationId: location.locationId,
                locationCode: location.locationCode,
                locationName: location.locationName,
                isActive: location.isActive,
              }));

        setMetadata({ ...result, locations });
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(getUserManagementErrorMessage(loadError, 'Unable to load user-management access.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMetadata(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const loadUsers = useCallback(async () => {
    if (!metadata) return;
    setLoadingUsers(true);
    setError(null);
    try {
      const result = await getManagedUsers({
        search: debouncedSearch || undefined,
        roleId: roleId || undefined,
        locationId: locationId || undefined,
        pageNumber,
        pageSize,
      });
      setUsersPage(result);
    } catch (loadError) {
      setError(getUserManagementErrorMessage(loadError, 'Unable to load EEAS users.'));
    } finally {
      setLoadingUsers(false);
    }
  }, [debouncedSearch, locationId, metadata, pageNumber, pageSize, roleId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!metadata?.capabilities.canAssignRoles || debouncedSearch.trim().length < 2) {
      setDirectoryEmployees([]);
      setDirectoryError(null);
      return;
    }

    let cancelled = false;
    setLoadingDirectory(true);
    searchEmployeeDirectory(debouncedSearch, {
      limit: 10,
      includeProvisioned: false,
    })
      .then((results) => {
        if (!cancelled) {
          setDirectoryEmployees(results.filter((employee) => employee.status === 'A'));
          setDirectoryError(null);
        }
      })
      .catch((searchError) => {
        if (!cancelled) {
          setDirectoryEmployees([]);
          setDirectoryError(getUserManagementErrorMessage(
            searchError,
            'Unable to search the Exponent employee directory.',
          ));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDirectory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, metadata?.capabilities.canAssignRoles]);

  useEffect(() => {
    if (!metadata) return;
    let cancelled = false;
    getLatestEmployeeReconciliation()
      .then((result) => {
        if (!cancelled) setLatestReconciliation(result);
      })
      .catch(() => {
        if (!cancelled) setLatestReconciliation(null);
      });
    return () => {
      cancelled = true;
    };
  }, [metadata]);

  useEffect(() => {
    if (activeTab !== 1 || roleMatrix.length > 0 || !metadata) return;
    setLoadingRoleMatrix(true);
    getUserManagementRoleMatrix()
      .then(setRoleMatrix)
      .catch((loadError) =>
        setError(getUserManagementErrorMessage(loadError, 'Unable to load the role matrix.')),
      )
      .finally(() => setLoadingRoleMatrix(false));
  }, [activeTab, metadata, roleMatrix.length]);

  const openUser = async (user: ManagedUser) => {
    setSelectedUser(user);
    setDrawerOpen(true);
    try {
      setSelectedUser(await getManagedUser(user.appUserId));
    } catch (loadError) {
      setError(getUserManagementErrorMessage(loadError, 'Unable to load user details.'));
    }
  };

  const manageExistingUser = async (appUserId: string) => {
    setAddDialogOpen(false);
    setEmployeeToAdd(null);
    try {
      setSelectedUser(await getManagedUser(appUserId));
      setDrawerOpen(true);
    } catch (loadError) {
      setError(getUserManagementErrorMessage(
        loadError,
        'Unable to load this employee’s EEAS role assignments.',
      ));
    }
  };

  const handleUpdated = (user: ManagedUser, message: string) => {
    setSelectedUser(user);
    setSnackbar({ open: true, message });
    loadUsers();
  };

  const handleCreated = (user: ManagedUser) => {
    setAddDialogOpen(false);
    setEmployeeToAdd(null);
    setSelectedUser(user);
    setDrawerOpen(true);
    setSnackbar({ open: true, message: `${managedUserDisplayName(user.displayName)} was added to EEAS.` });
    setPageNumber(1);
    loadUsers();
  };

  const clearFilters = () => {
    setSearchText('');
    setDebouncedSearch('');
    setRoleId('');
    setLocationId('');
    setPageNumber(1);
  };

  const activeFilterCount = [debouncedSearch, roleId, locationId].filter(Boolean).length;
  if (isSecurityLoading || (!security && !securityError) || loadingMetadata) {
    return (
      <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
        <LoadingSpinner text="Loading user management…" />
      </Box>
    );
  }

  if (!canView || securityError) return <AccessDeniedPage />;

  if (!metadata) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error ?? 'User management is unavailable.'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: 3, maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <AppBreadcrumbs items={[
        { label: 'Settings', path: '/settings' },
        { label: 'Users & Roles' },
      ]} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Users &amp; Roles
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.5 }}>
            Manage EEAS user access, role assignments, and location scope.
          </Typography>
          {latestReconciliation && (
            <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mt: 0.5 }}>
              Last employee reconciliation: {formatDate(
                latestReconciliation.completedAtUtc ?? latestReconciliation.startedAtUtc,
              )} · {latestReconciliation.status}
              {latestReconciliation.isDryRun ? ' (dry run)' : ''}
              {' · '}{latestReconciliation.usersExamined} users checked
            </Typography>
          )}
        </Box>
        {metadata.capabilities.canAssignRoles && (
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              startIcon={<PersonAddAltOutlinedIcon />}
              onClick={() => {
                setEmployeeToAdd(null);
                setAddDialogOpen(true);
              }}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Add User
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIndOutlinedIcon />}
              disabled={!selectedUser}
              onClick={() => selectedUser && setDrawerOpen(true)}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Assign Roles
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" action={<Button color="inherit" onClick={loadUsers}>Retry</Button>} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          bgcolor: appColors.surface.card,
          border: `1px solid ${appColors.border.default}`,
          borderRadius: '10px',
          p: 1,
          mb: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(240px, 2fr) 1fr', lg: 'minmax(280px, 2fr) 1fr 1fr auto' },
          gap: 1,
        }}
      >
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search by name, email, or username"
          slotProps={{
            htmlInput: { 'aria-label': 'Search users' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small">
          <Select
            displayEmpty
            value={roleId}
            onChange={(event) => { setRoleId(event.target.value); setPageNumber(1); }}
            inputProps={{ 'aria-label': 'Filter by role' }}
          >
            <MenuItem value="">All Roles</MenuItem>
            {metadata.roles.map((role) => (
              <MenuItem key={role.appRoleId} value={role.appRoleId}>
                {roleLabel(role.roleName)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <Select
            displayEmpty
            value={locationId}
            onChange={(event) => { setLocationId(event.target.value); setPageNumber(1); }}
            inputProps={{ 'aria-label': 'Filter by location' }}
          >
            <MenuItem value="">All Locations</MenuItem>
            {metadata.locations.map((location) => (
              <MenuItem key={location.locationId} value={location.locationId}>
                {location.locationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={clearFilters}
          disabled={activeFilterCount === 0}
          startIcon={<FilterAltOffOutlinedIcon />}
        >
          Clear{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
      </Box>

      {metadata.capabilities.canAssignRoles && debouncedSearch.trim().length >= 2 && (
        <Card
          variant="outlined"
          sx={{ borderRadius: '10px', mb: 2, bgcolor: appColors.surface.card }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: 1.5,
                mb: 1.5,
                border: '1px solid rgba(46, 139, 132, 0.22)',
                borderRadius: '8px',
                bgcolor: 'rgba(46, 139, 132, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: '8px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(46, 139, 132, 0.14)',
                    color: appColors.primary.main,
                  }}
                >
                  <GroupOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: appColors.primary.main }}
                  >
                    Exponent employee directory
                  </Typography>
                  <Typography variant="caption" sx={{ color: appColors.text.body }}>
                    Active employees not currently linked to an EEAS user.
                  </Typography>
                </Box>
              </Box>
              {loadingDirectory && <CircularProgress size={20} />}
            </Box>
            {directoryError && <Alert severity="warning">{directoryError}</Alert>}
            {!directoryError && !loadingDirectory && directoryEmployees.length === 0 && (
              <Typography variant="body2" sx={{ color: appColors.text.body }}>
                No unprovisioned employees matched this search.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {directoryEmployees.map((employee) => (
                <Box
                  key={employee.employeeId}
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    borderTop: `1px solid ${appColors.border.default}`,
                    pt: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {employee.displayName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                      {employee.upn || employee.businessEmail || 'No email'} · Employee {employee.employeeId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="HR Active"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!employee.canProvision}
                      onClick={() => {
                        setEmployeeToAdd(employee);
                        setAddDialogOpen(true);
                      }}
                    >
                      Add to EEAS
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ borderBottom: `1px solid ${appColors.border.default}`, mb: 2 }}
      >
        <Tab label="App Users" />
        <Tab label="Role Matrix" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Box sx={{ position: 'relative', minHeight: 220 }}>
            {loadingUsers && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(245,245,245,0.72)',
                  zIndex: 2,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <TableContainer
              sx={{
                display: { xs: 'none', md: 'block' },
                bgcolor: appColors.surface.card,
                border: `1px solid ${appColors.border.default}`,
                borderRadius: '10px',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>Locations</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersPage?.items.map((user) => {
                    const selected = selectedUser?.appUserId === user.appUserId;
                    return (
                      <TableRow
                        key={user.appUserId}
                        hover
                        selected={selected}
                        onClick={() => openUser(user)}
                        sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: 'rgba(46, 139, 132, 0.10)' } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 30, height: 30, bgcolor: appColors.primary.main, fontSize: '0.7rem' }}>
                              {initialsFor(managedUserDisplayName(user.displayName))}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {managedUserDisplayName(user.displayName)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: appColors.text.caption }}>{user.userName}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {user.roleNames.map((role) => <RoleChip key={role} roleName={role} />)}
                            {user.roleNames.length === 0 && <Typography variant="caption">No roles</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {user.locationNames.length > 0 ? user.locationNames.join(', ') : 'Global / none'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                            <Chip
                              label={user.isActive ? 'Access enabled' : user.isAccessSuspended ? 'Suspended' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor: user.isActive ? appColors.status.successBg : '#FFEBEE',
                                color: user.isActive ? appColors.status.success : appColors.status.error,
                                fontWeight: 700,
                              }}
                            />
                            {user.hrStatus && (
                              <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                                HR: {user.hrStatus === 'A' ? 'Active' : user.hrStatus === 'T' ? 'Terminated' : user.hrStatus}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateOnly(user.lastUpdatedAtUtc)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Manage user">
                            <IconButton
                              size="small"
                              aria-label={`Manage ${managedUserDisplayName(user.displayName)}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openUser(user);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
              {usersPage?.items.map((user) => (
                <Card
                  key={user.appUserId}
                  variant="outlined"
                  onClick={() => openUser(user)}
                  sx={{ borderRadius: '10px', cursor: 'pointer' }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Avatar sx={{ width: 38, height: 38, bgcolor: appColors.primary.main, fontSize: '0.75rem' }}>
                          {initialsFor(managedUserDisplayName(user.displayName))}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                            {managedUserDisplayName(user.displayName)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: appColors.text.body }} noWrap>{user.email}</Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={user.isActive ? 'Access enabled' : user.isAccessSuspended ? 'Suspended' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: user.isActive ? appColors.status.successBg : '#FFEBEE',
                          color: user.isActive ? appColors.status.success : appColors.status.error,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                      {user.roleNames.map((role) => <RoleChip key={role} roleName={role} />)}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.25, color: appColors.text.caption }}>
                      <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {user.locationNames.length > 0 ? user.locationNames.join(', ') : 'Global / no location'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {!loadingUsers && usersPage?.items.length === 0 && (
              <Box sx={{ py: 7, textAlign: 'center', bgcolor: appColors.surface.card, borderRadius: '10px' }}>
                <GroupOutlinedIcon sx={{ fontSize: 48, color: appColors.text.placeholder, mb: 1 }} />
                <Typography variant="h6">No users found</Typography>
                <Typography variant="body2" sx={{ color: appColors.text.body }}>
                  Adjust the filters or add a user within your authorized scope.
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            }}
          >
            <Typography variant="caption" sx={{ color: appColors.text.body }}>
              {usersPage?.totalCount
                ? `Showing ${(usersPage.pageNumber - 1) * usersPage.pageSize + 1}–${Math.min(usersPage.pageNumber * usersPage.pageSize, usersPage.totalCount)} of ${usersPage.totalCount} users`
                : 'No users to display'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small">
                <Select
                  value={String(pageSize)}
                  onChange={(event) => { setPageSize(Number(event.target.value)); setPageNumber(1); }}
                  inputProps={{ 'aria-label': 'Users per page' }}
                >
                  <MenuItem value="10">10 per page</MenuItem>
                  <MenuItem value="25">25 per page</MenuItem>
                  <MenuItem value="50">50 per page</MenuItem>
                </Select>
              </FormControl>
              <Pagination
                count={Math.max(1, usersPage?.totalPages ?? 1)}
                page={pageNumber}
                onChange={(_, value) => setPageNumber(value)}
                color="primary"
                size="small"
              />
            </Box>
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <Box>
          {loadingRoleMatrix ? (
            <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 1.5,
              }}
            >
              {roleMatrix.map((role) => (
                <Card key={role.appRoleId} variant="outlined" sx={{ borderRadius: '10px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {roleLabel(role.roleName)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: appColors.text.body }}>
                          {role.roleDescription}
                        </Typography>
                      </Box>
                      <Chip
                        label={role.canAssign ? 'Assignable' : 'Protected'}
                        size="small"
                        color={role.canAssign ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 0.75 }}>
                      Permissions
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {role.permissions.map((permission) => (
                        <Tooltip key={permission.appPermissionId} title={permission.permissionDescription ?? ''}>
                          <Chip label={permission.permissionName} size="small" variant="outlined" />
                        </Tooltip>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      <AddManagedUserDialog
        open={addDialogOpen}
        metadata={metadata}
        initialEmployee={employeeToAdd}
        onClose={() => {
          setAddDialogOpen(false);
          setEmployeeToAdd(null);
        }}
        onCreated={handleCreated}
        onManageExisting={manageExistingUser}
      />
      <ManagedUserDrawer
        user={selectedUser}
        metadata={metadata}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdated={handleUpdated}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsUsersPage;
